import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { useToast, usePermissions } from '../../hooks';
import { getLead } from '../../api/leads';
import {
  getProposal,
  getProposalProcessingStatus,
  getWorkerOutputs,
  getReviews,
  submitReview,
  addSectionComment,
  deleteSectionComment,
  updateSectionContent,
  reviseProposal,
  dismissAllSectionComments,
  submitForReview,
  cancelReview,
  sendToClient,
  sendBack,
  generateNarrations,
  updateNarrations,
} from '../../api/proposals';
import {
  ProposalStatusBadge,
  ProposalGenerationProgress,
  ProposalSectionCard,
  SectionDataPanel,
  ProposalRatingBadge,
  SendToClientModal,
  NarrationEditorModal,
} from '../../components/proposal';
import PromptVersionsBadge from '../../components/ui/PromptVersionsBadge';
import { ConfirmationModal } from '../../components/ui/Modal';

const PROPOSAL_PROMPT_LABELS = {
  origination_worker: 'Origination',
  regulatory_structuring_worker: 'Regulatory',
  technology_worker: 'Technology',
  distribution_worker: 'Distribution',
  proposal_orchestrator: 'Orchestrator',
  proposal_rating: 'Rating',
};
const PROPOSAL_PROMPT_ORDER = [
  'origination_worker',
  'regulatory_structuring_worker',
  'technology_worker',
  'distribution_worker',
  'proposal_orchestrator',
  'proposal_rating',
];

/** Serialize sections by number so a revision can be diffed field-for-field. */
const snapshotSections = (sections) => {
  const snap = {};
  for (const s of Array.isArray(sections) ? sections : []) {
    if (s?.section_number == null) continue;
    snap[s.section_number] = JSON.stringify({
      title: s.title ?? null,
      content: s.content ?? null,
      subsections: s.subsections ?? null,
      key_metrics: s.key_metrics ?? null,
    });
  }
  return snap;
};

/** Section numbers whose content differs between two snapshots, ascending. */
const changedSectionNumbers = (before, after) =>
  Object.keys(after)
    .filter((num) => before[num] !== after[num])
    .map(Number)
    .sort((a, b) => a - b);

/* --- Main Page --- */
const ProposalDetailPage = () => {
  const { leadId, proposalId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [lead, setLead] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [sectionComments, setSectionComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  // In-place revision runs on the same page — unlike generation it must not swap in
  // the full-page worker-progress screen.
  const [isRevising, setIsRevising] = useState(false);
  const [generatingStartTime] = useState(Date.now());
  const [questionnaire, setQuestionnaire] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [commentLoading, setCommentLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionComment, setActionComment] = useState('');
  const [actionRating, setActionRating] = useState(5);
  const [actionLoading, setActionLoading] = useState(null);
  const [revisionConfirm, setRevisionConfirm] = useState(null);
  const [revisionLoading, setRevisionLoading] = useState(false);
  const [showRevisionConfirm, setShowRevisionConfirm] = useState(false);
  const [revisionAllLoading, setRevisionAllLoading] = useState(false);
  const [cancelRevisionLoading, setCancelRevisionLoading] = useState(false);
  const [showCancelRevisionConfirm, setShowCancelRevisionConfirm] = useState(false);
  const [submitForReviewLoading, setSubmitForReviewLoading] = useState(false);
  const [cancelReviewLoading, setCancelReviewLoading] = useState(false);
  const [sendToClientLoading, setSendToClientLoading] = useState(false);
  const [showSendToClientModal, setShowSendToClientModal] = useState(false);
  const [showSubmitForReviewDialog, setShowSubmitForReviewDialog] = useState(false);
  const [submitReviewComment, setSubmitReviewComment] = useState('');
  const [showSendBackDialog, setShowSendBackDialog] = useState(false);
  const [sendBackComment, setSendBackComment] = useState('');
  const [sendBackLoading, setSendBackLoading] = useState(false);
  const [showNarrationModal, setShowNarrationModal] = useState(false);
  const [presentationMenuOpen, setPresentationMenuOpen] = useState(false);
  const [narrationSaving, setNarrationSaving] = useState(false);
  const [narrationGenerating, setNarrationGenerating] = useState(false);
  const sectionRefs = useRef({});
  // Snapshot of the sections taken when a revision is requested, so the completion
  // toast can name every section the reviser actually touched — including ones the
  // user never commented on (factual corrections propagate).
  const preRevisionSectionsRef = useRef(null);

  // Permissions
  const { can, isAdmin } = usePermissions();
  const canApprove = can.approveProposal();
  const canSubmitForReview = can.submitForReview();
  const manualSelectRef = useRef(false);

  // IntersectionObserver — auto-select the most visible section on scroll
  useEffect(() => {
    const ratioMap = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        // Skip auto-select briefly after a manual click
        if (manualSelectRef.current) return;

        for (const entry of entries) {
          ratioMap.set(entry.target, entry.intersectionRatio);
        }

        let best = null;
        let bestRatio = 0;
        for (const [el, ratio] of ratioMap) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            best = el;
          }
        }
        if (best && bestRatio > 0) {
          const num = parseInt(best.id.replace('section-', ''), 10);
          if (!isNaN(num)) setActiveSection(num);
        }
      },
      { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1], rootMargin: '-80px 0px -30% 0px' },
    );

    // Observe every section card currently in the DOM
    const els = Object.values(sectionRefs.current);
    els.forEach((el) => el && observer.observe(el));

    return () => observer.disconnect();
  }, [proposal?.sections?.length]); // re-run when sections load

  const handleManualSelect = useCallback((num) => {
    setActiveSection(num);
    manualSelectRef.current = true;
    setTimeout(() => { manualSelectRef.current = false; }, 600);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [leadRes, proposalRes] = await Promise.all([
        getLead(leadId),
        getProposal(leadId, proposalId),
      ]);
      setLead(leadRes.data);
      setProposal(proposalRes.data);
      setSectionComments(proposalRes.data?.section_comments || []);
      setQuestionnaire(proposalRes.data?.questionnaire_context || []);

      // Default active section to the first client-facing section (only if not already set)
      const secs = Array.isArray(proposalRes.data?.sections) ? proposalRes.data.sections : [];
      if (secs.length > 0) {
        const sorted = [...secs].sort((a, b) => (a.section_number || 0) - (b.section_number || 0));
        setActiveSection((prev) => prev ?? sorted[0].section_number);
      }

      // Resume polling if a revision is still running (page reload mid-revision)
      if (proposalRes.data?.revision_status === 'revising') {
        if (!preRevisionSectionsRef.current) {
          preRevisionSectionsRef.current = snapshotSections(secs);
        }
        setIsRevising(true);
      }

      if (['queued', 'workers_running', 'rating_proposal', 'generating_proposal'].includes(proposalRes.data.generation_status)) {
        setIsGenerating(true);
      } else {
        setIsGenerating(false);
        try {
          const [workersRes, reviewsRes] = await Promise.all([
            getWorkerOutputs(leadId, proposalId),
            getReviews(leadId, proposalId),
          ]);
          setWorkers(workersRes.data?.worker_outputs || []);
          setReviews(reviewsRes.data?.reviews || []);
        } catch { /* optional data */ }
      }
      return proposalRes.data;
    } catch (err) {
      showToast('Failed to load proposal', 'error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [leadId, proposalId, showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // One poll loop for both background jobs. Generation swaps the whole page for the
  // worker-progress screen; revision keeps the page and only disables the actions.
  useEffect(() => {
    if (!isGenerating && !isRevising) return;
    const interval = setInterval(async () => {
      try {
        const res = await getProposalProcessingStatus(leadId, proposalId);
        const status = res.data || {};

        if (isGenerating) {
          // Fetch live worker outputs
          try {
            const workersRes = await getWorkerOutputs(leadId, proposalId);
            setWorkers(workersRes.data?.worker_outputs || []);
          } catch { /* may 404 while still early in generation */ }

          if (!status.is_processing) {
            setIsGenerating(false);
            fetchData();
          }
        }

        if (isRevising && status.revision_status !== 'revising') {
          setIsRevising(false);
          const before = preRevisionSectionsRef.current || {};
          preRevisionSectionsRef.current = null;

          if (status.revision_status === 'failed') {
            // Comments were never closed — they are still visible and still open.
            showToast(status.revision_error || 'Revision failed. Your comments are still open — try again.', 'error');
            fetchData();
          } else {
            const updated = await fetchData();
            const changed = changedSectionNumbers(before, snapshotSections(updated?.sections));
            // Name every changed section, including ones the user did not comment on,
            // so propagation reads as intended behaviour rather than the AI going rogue.
            showToast(
              changed.length > 0
                ? `Revision applied — Section${changed.length !== 1 ? 's' : ''} ${changed.join(', ')} updated`
                : 'Revision applied — no section content changed',
              'success'
            );
          }
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [isGenerating, isRevising, leadId, proposalId, fetchData, showToast]);

  const handleAddSectionComment = async (commentData) => {
    setCommentLoading(true);
    try {
      const res = await addSectionComment(leadId, proposalId, commentData);
      setSectionComments((prev) => [...prev, res.data]);
    } catch (err) {
      showToast('Failed to add comment', 'error');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteSectionComment(leadId, proposalId, commentId);
      setSectionComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to remove comment', 'error');
    }
  };

  const handleUpdateSectionContent = async (sectionNumber, contentData) => {
    try {
      await updateSectionContent(leadId, proposalId, sectionNumber, contentData);
      showToast(`Section ${sectionNumber} updated`, 'success');
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update section', 'error');
    }
  };

  const handleRequestRevision = () => {
    setShowRevisionConfirm(true);
  };

  // The server reads the stored comments; the body only says which of them to act on.
  const handleConfirmRevisionAll = async () => {
    setRevisionAllLoading(true);
    try {
      preRevisionSectionsRef.current = snapshotSections(proposal?.sections);
      await reviseProposal(leadId, proposalId, { section_numbers: null });
      setIsRevising(true);
      showToast('Revision started — applying your comments', 'success');
      setShowRevisionConfirm(false);
    } catch (err) {
      preRevisionSectionsRef.current = null;
      showToast(err.response?.data?.detail || 'Failed to request revision', 'error');
    } finally {
      setRevisionAllLoading(false);
    }
  };

  const handleCancelRevision = () => {
    setShowCancelRevisionConfirm(true);
  };

  const handleConfirmCancelRevision = async () => {
    setCancelRevisionLoading(true);
    try {
      await dismissAllSectionComments(leadId, proposalId);
      setSectionComments((prev) => prev.map((c) =>
        c.status === 'open' ? { ...c, status: 'dismissed' } : c
      ));
      showToast('All open comments dismissed', 'success');
      setShowCancelRevisionConfirm(false);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to cancel revision', 'error');
    } finally {
      setCancelRevisionLoading(false);
    }
  };

  const handleSectionRevisionRequest = (sectionNumber) => {
    setRevisionConfirm(sectionNumber);
  };

  const handleConfirmSectionRevision = async () => {
    const sectionNumber = revisionConfirm;
    setRevisionLoading(true);
    try {
      preRevisionSectionsRef.current = snapshotSections(proposal?.sections);
      await reviseProposal(leadId, proposalId, { section_numbers: [sectionNumber] });
      setIsRevising(true);
      showToast(`Revision started — applying your Section ${sectionNumber} comments`, 'success');
      setRevisionConfirm(null);
    } catch (err) {
      preRevisionSectionsRef.current = null;
      showToast(err.response?.data?.detail || 'Failed to request section revision', 'error');
    } finally {
      setRevisionLoading(false);
    }
  };

  const handleSubmitForReview = async () => {
    setSubmitForReviewLoading(true);
    try {
      await submitForReview(leadId, proposalId, {
        comment: submitReviewComment.trim() || null,
      });
      showToast('Proposal submitted for admin review', 'success');
      setShowSubmitForReviewDialog(false);
      setSubmitReviewComment('');
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to submit for review', 'error');
    } finally {
      setSubmitForReviewLoading(false);
    }
  };

  const handleSendToClient = async (emailData) => {
    setSendToClientLoading(true);
    try {
      await sendToClient(leadId, proposalId, emailData);
      showToast('Proposal sent to client successfully', 'success');
      setShowSendToClientModal(false);
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to send to client', 'error');
    } finally {
      setSendToClientLoading(false);
    }
  };

  const handleSendBack = async () => {
    setSendBackLoading(true);
    try {
      await sendBack(leadId, proposalId, {
        comment: sendBackComment.trim(),
      });
      showToast('Proposal sent back for updates', 'success');
      setShowSendBackDialog(false);
      setSendBackComment('');
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to send back', 'error');
    } finally {
      setSendBackLoading(false);
    }
  };

  const handleCancelReview = async () => {
    setCancelReviewLoading(true);
    try {
      await cancelReview(leadId, proposalId);
      showToast('Review request cancelled', 'success');
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to cancel review', 'error');
    } finally {
      setCancelReviewLoading(false);
    }
  };

  const openActionDialog = (type) => {
    setActionDialog(type);
    setActionComment('');
    setActionRating(5);
  };

  const closeActionDialog = () => {
    setActionDialog(null);
    setActionComment('');
    setActionRating(5);
  };

  const handleConfirmAction = async () => {
    const type = actionDialog;
    const decision = type === 'approve' ? 'approved' : 'rejected';
    setActionLoading(type);
    try {
      await submitReview(leadId, proposalId, {
        decision,
        comment: actionComment.trim() || `${decision.charAt(0).toUpperCase() + decision.slice(1)} via proposal review`,
        human_rating: actionRating,
      });
      showToast(`Proposal ${decision}`, 'success');
      closeActionDialog();
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || `Failed to ${type}`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const narrationPollRef = useRef(null);
  const narrationTimeoutRef = useRef(null);

  // Clean up narration polling on unmount
  useEffect(() => {
    return () => {
      if (narrationPollRef.current) clearInterval(narrationPollRef.current);
      if (narrationTimeoutRef.current) clearTimeout(narrationTimeoutRef.current);
    };
  }, []);

  const handleGenerateNarrations = async () => {
    setNarrationGenerating(true);
    try {
      await generateNarrations(leadId, proposalId);
      showToast('Narration generation started. This may take a minute...', 'success');
      // Poll for completion every 5 seconds
      narrationPollRef.current = setInterval(async () => {
        try {
          const res = await getProposal(leadId, proposalId);
          const data = res.data || res;
          if (data.narrations && data.narrations.length > 0 && !data.narrations_stale) {
            clearInterval(narrationPollRef.current);
            narrationPollRef.current = null;
            clearTimeout(narrationTimeoutRef.current);
            narrationTimeoutRef.current = null;
            setNarrationGenerating(false);
            showToast('Narration generated successfully', 'success');
            fetchData();
          }
        } catch {
          // ignore polling errors
        }
      }, 5000);
      // Stop polling after 2 minutes
      narrationTimeoutRef.current = setTimeout(() => {
        if (narrationPollRef.current) clearInterval(narrationPollRef.current);
        narrationPollRef.current = null;
        narrationTimeoutRef.current = null;
        setNarrationGenerating(false);
      }, 120000);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to generate narration', 'error');
      setNarrationGenerating(false);
    }
  };

  const handleSaveNarrations = async (narrations) => {
    setNarrationSaving(true);
    try {
      await updateNarrations(leadId, proposalId, narrations);
      showToast('Narration saved', 'success');
      setShowNarrationModal(false);
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save narration', 'error');
    } finally {
      setNarrationSaving(false);
    }
  };

  /* --- Loading state --- */
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  /* --- Generating state --- */
  if (isGenerating) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto py-6 px-4">
          <ProposalGenerationProgress
            status={proposal?.generation_status || 'queued'}
            workersCompleted={workers.length}
            workerOutputs={workers}
            startTime={generatingStartTime}
            onRetry={() => navigate(`/leads/${leadId}?tab=proposals`)}
            rating={proposal?.rating}
            ratingReasons={proposal?.rating_reasons}
          />
        </div>
      </DashboardLayout>
    );
  }

  const sections = Array.isArray(proposal?.sections) ? proposal.sections : [];
  const isActive = proposal?.is_active === true;
  const isDraft = proposal?.status === 'draft';
  const isPendingQA = proposal?.status === 'pending_qa';
  const isApproved = proposal?.status === 'approved';
  const isSentBack = proposal?.status === 'sent_back';
  const isSentToClient = proposal?.status === 'sent_to_client';
  const isClientRejected = proposal?.status === 'client_rejected';
  const isAccepted = proposal?.status === 'accepted';
  const canSendToClient = can.sendToClient();
  // Read-only oversight roles (viewer, compliance_officer) hold no proposal write
  // permission, so every write action below is hidden for them.
  const canModifyProposal = canApprove || canSubmitForReview || canSendToClient;
  const canAct = canModifyProposal && isActive && (isDraft || isPendingQA || isApproved || isSentBack || isClientRejected);
  const openCommentCount = sectionComments.filter((c) => c.status === 'open').length;
  const revisionRound = proposal?.revision_round || 0;
  const lastRevisedAt = proposal?.last_revised_at ? new Date(proposal.last_revised_at) : null;

  // Find the approval review for "Approved by" display
  const approvalReview = (isApproved || isSentToClient) && reviews.length > 0
    ? reviews.find((r) => r.decision === 'approved')
    : null;

  return (
    <DashboardLayout>
      <div className="pb-24">
        {/* ===== HEADER ===== */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <button
                onClick={() => navigate(`/leads/${leadId}?tab=proposals`)}
                className="text-sm text-gray-400 hover:text-gray-600 mb-1 inline-flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Lead
              </button>
              <h1 className="text-xl font-bold text-gray-900">{proposal?.title || 'Proposal'}</h1>
              {lead && <p className="text-sm text-gray-500 mt-0.5">{lead.company} &middot; {lead.name}</p>}
              {isRevising && (
                <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-orange-600">
                  <span className="w-3.5 h-3.5 border-2 border-orange-300 border-t-orange-600 rounded-full animate-spin"></span>
                  Revising… applying your comments in place. This can take a minute.
                </p>
              )}
            </div>
            <div className="flex items-center flex-wrap gap-2 sm:flex-shrink-0">
              {/* Version indicator — a revision edits in place, so the version never moves */}
              <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2 py-1 bg-white">
                <span className="text-sm font-bold text-gray-900 leading-none">v{proposal?.version || 1}</span>
                {revisionRound > 0 && (
                  <span
                    className="text-[11px] font-medium text-gray-500 leading-none"
                    title={lastRevisedAt ? `Last revised ${lastRevisedAt.toLocaleString()}` : undefined}
                  >
                    Revised {revisionRound} time{revisionRound !== 1 ? 's' : ''}
                    {lastRevisedAt ? ` · last revised ${lastRevisedAt.toLocaleDateString()}` : ''}
                  </span>
                )}
                {isActive ? (
                  <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                    Active
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                    Inactive
                  </span>
                )}
              </div>

              {/* Divider */}
              <div className="w-px h-6 bg-gray-200"></div>

              {/* Status — monochrome pill */}
              <ProposalStatusBadge status={proposal?.status} size="md" />

              {/* Rating — keeps popup functionality */}
              {proposal?.rating != null && (
                <ProposalRatingBadge rating={proposal.rating} reasons={proposal.rating_reasons} size="md" showPopup />
              )}

              {/* Prompt versions — hover tooltip for debugging */}
              <PromptVersionsBadge
                promptsUsed={proposal?.prompts_used}
                currentPromptVersions={proposal?.current_prompt_versions}
                labels={PROPOSAL_PROMPT_LABELS}
                order={PROPOSAL_PROMPT_ORDER}
                contextNoun="proposal"
                align="right"
              />

              {/* Presentation dropdown — Preview, Narrations, Cost */}
              {(proposal?.client_review_url || proposal?.avatar_enabled) && (
                <div className="relative ml-auto sm:ml-0">
                  <button
                    onClick={() => setPresentationMenuOpen(!presentationMenuOpen)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
                    </svg>
                    Presentation
                    {proposal?.narrations_stale && <span className="w-2 h-2 bg-amber-400 rounded-full"></span>}
                    <svg className={`w-3.5 h-3.5 transition-transform ${presentationMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {presentationMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setPresentationMenuOpen(false)} />
                      <div className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-1">
                        {/* Preview */}
                        {proposal?.client_review_url && (
                          <a
                            href={proposal.client_review_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setPresentationMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <div>
                              <div className="font-medium">Preview</div>
                              <div className="text-xs text-gray-400">Open client view</div>
                            </div>
                          </a>
                        )}

                        {/* Narrations */}
                        {proposal?.avatar_enabled && (
                          <>
                            <div className="border-t border-gray-100 my-1" />
                            <button
                              onClick={() => { setPresentationMenuOpen(false); setShowNarrationModal(true); }}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full text-left"
                            >
                              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                              </svg>
                              <div className="flex-1">
                                <div className="font-medium flex items-center gap-2">
                                  Narration
                                  {proposal?.narrations_stale && <span className="w-2 h-2 bg-amber-400 rounded-full"></span>}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {proposal?.narrations
                                    ? <span className="text-green-600">{proposal.narrations.length} narration ready</span>
                                    : <span className="text-amber-600">Not generated — click to create</span>
                                  }
                                </div>
                              </div>
                            </button>

                            {/* Presenter Cost — hidden for now
                            {proposal?.avatar_sessions?.session_count > 0 && (
                              <>
                                <div className="border-t border-gray-100 my-1" />
                                <div className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-500">
                                  <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <div>
                                    <div className="font-medium text-gray-700">${Number(proposal.avatar_sessions.total_cost_usd || 0).toFixed(2)}</div>
                                    <div className="text-xs text-gray-400">{proposal.avatar_sessions.session_count} session{proposal.avatar_sessions.session_count > 1 ? 's' : ''} &middot; {Math.round((proposal.avatar_sessions.total_duration_seconds || 0) / 60)} min</div>
                                  </div>
                                </div>
                              </>
                            )}
                            */}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>

        {/* ===== STATUS BANNERS ===== */}
        {isActive && isPendingQA && !canApprove && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <svg className="w-5 h-5 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-blue-800">
                <span className="font-semibold">This proposal is under admin review.</span>{' '}
                You can cancel the review request to make changes.
              </p>
            </div>
          </div>
        )}
        {isActive && isPendingQA && canApprove && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">This proposal was submitted for your review.</span>{' '}
                  Please approve or reject it below.
                </p>
              </div>
              {proposal?.review_comment && (
                <div className="mt-2 ml-8 bg-white border border-blue-100 rounded-lg px-3 py-2">
                  <p className="text-xs font-medium text-blue-600 mb-0.5">Submission note:</p>
                  <p className="text-sm text-gray-700">{proposal.review_comment}</p>
                </div>
              )}
            </div>
          </div>
        )}
        {isApproved && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <svg className="w-5 h-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm text-green-800">
                <span className="font-semibold">
                  Approved{approvalReview?.reviewer_name ? ` by ${approvalReview.reviewer_name}` : ''}.
                </span>{' '}
                Ready to send to client.
              </p>
            </div>
          </div>
        )}
        {isSentBack && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-orange-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-orange-800">
                  <span className="font-semibold">This proposal was sent back for updates.</span>{' '}
                  Add comments to sections and request a revision — the proposal is updated in place, without a new version.
                </p>
              </div>
              {proposal?.review_comment && (
                <div className="mt-2 ml-8 bg-white border border-orange-100 rounded-lg px-3 py-2">
                  <p className="text-xs font-medium text-orange-600 mb-0.5">Admin feedback:</p>
                  <p className="text-sm text-gray-700">{proposal.review_comment}</p>
                </div>
              )}
            </div>
          </div>
        )}
        {isClientRejected && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4">
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-800">
                  <span className="font-semibold">The client sent this proposal back for changes.</span>{' '}
                  Review client comments below, add section comments, and request a revision — the proposal is updated in place, without a new version.
                </p>
              </div>
              {proposal?.client_decision_comment && (
                <div className="mt-2 ml-8">
                  <div className="bg-white border border-red-100 rounded-lg px-3 py-2">
                    <p className="text-xs font-medium text-red-600 mb-0.5">Client feedback:</p>
                    <p className="text-sm text-gray-700">{proposal.client_decision_comment}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {isSentToClient && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <svg className="w-5 h-5 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              <div className="flex-1 flex items-center justify-between">
                <p className="text-sm text-indigo-800">
                  <span className="font-semibold">This proposal has been sent to the client.</span>
                </p>
                {proposal?.client_review_url && (
                  <a
                    href={proposal.client_review_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700 hover:text-indigo-900 hover:underline transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                    Client View
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {isAccepted && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-green-800">
                  <span className="font-semibold">The client has accepted this proposal.</span>
                </p>
              </div>
              {proposal?.client_decision_comment && (
                <div className="mt-2 ml-8">
                  <div className="bg-white border border-green-100 rounded-lg px-3 py-2">
                    <p className="text-xs font-medium text-green-600 mb-0.5">Client feedback:</p>
                    <p className="text-sm text-gray-700">{proposal.client_decision_comment}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== INACTIVE BANNER ===== */}
        {!isActive && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <svg className="w-5 h-5 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm text-amber-800">
                <span className="font-semibold">This is not the active version.</span>{' '}
                Approval and rejection actions are only available on the active proposal version. You can set this as the active version from the lead detail page.
              </p>
            </div>
          </div>
        )}

        {/* ===== EXECUTIVE SUMMARY (full-width) ===== */}
        {proposal?.executive_summary && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-6">
            <div className="bg-primary-50 border border-primary-100 rounded-xl p-6">
              <h2 className="text-base font-semibold text-primary-900 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Executive Summary
              </h2>
              <p className="text-gray-700 leading-relaxed text-sm">{proposal.executive_summary}</p>
            </div>
          </div>
        )}

        {/* ===== TWO-COLUMN: SECTION CARDS + DATA PANEL ===== */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-6">
          {/* Column headings */}
          <div className="hidden md:flex gap-6 mb-3">
            <div className="w-[60%]">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Proposal Content</h3>
            </div>
            <div className="w-[40%]">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Section Reasoning</h3>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Left column: Section cards (60%) */}
            <div className="w-full md:w-[60%] space-y-5">
              {sections.length > 0 ? (
                sections
                  .sort((a, b) => (a.section_number || 0) - (b.section_number || 0))
                  .map((section) => (
                    <div
                      key={section.section_number}
                      ref={(el) => {
                        if (el) sectionRefs.current[section.section_number] = el;
                        else delete sectionRefs.current[section.section_number];
                      }}
                      id={`section-${section.section_number}`}
                    >
                      <ProposalSectionCard
                        section={section}
                        workers={workers}
                        comments={sectionComments}
                        clientComments={proposal?.client_comments || []}
                        onAddComment={(canModifyProposal && (isDraft || isPendingQA || isSentBack || isClientRejected)) ? handleAddSectionComment : undefined}
                        onDeleteComment={canModifyProposal ? handleDeleteComment : undefined}
                        onUpdateContent={(isDraft && canApprove) ? handleUpdateSectionContent : undefined}
                        onRequestRevision={(canAct && !isRevising && (isDraft || isSentBack || isClientRejected)) ? handleSectionRevisionRequest : undefined}
                        commentLoading={commentLoading}
                        isActive={activeSection === section.section_number}
                        onSelect={handleManualSelect}
                      />
                    </div>
                  ))
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                  <p className="text-gray-400 text-sm">No proposal content available.</p>
                </div>
              )}
            </div>

            {/* Right column: Data provenance panel (40%, sticky) */}
            <div className="w-full md:w-[40%] md:sticky md:top-20 md:max-h-[calc(100vh-120px)] md:overflow-y-auto">
              <SectionDataPanel
                section={sections.find((s) => s.section_number === activeSection) || null}
                workers={workers}
                proposal={proposal}
                questionnaire={questionnaire}
              />
            </div>
          </div>
        </div>

        {/* ===== FULL-WIDTH BELOW TWO COLUMNS ===== */}
        {reviews.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div id="qa-history" className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Review History</h2>
              <div className="space-y-2">
                {reviews.map((review) => (
                  <div key={review.id} className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center flex-wrap gap-2 min-w-0">
                      <ProposalStatusBadge status={review.decision === 'approved' ? 'approved_qa' : 'rejected_qa'} />
                      {review.human_rating != null && (
                        <span className="inline-flex items-center gap-1.5" title={`Reviewer rating: ${review.human_rating} out of 10`}>
                          <span className="text-xs font-medium text-gray-500">Rating</span>
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white ${
                            review.human_rating >= 7 ? 'bg-green-500' : review.human_rating >= 4 ? 'bg-amber-500' : 'bg-red-500'
                          }`}>
                            {review.human_rating}
                          </span>
                        </span>
                      )}
                      {review.comment && <p className="text-sm text-gray-600 break-words min-w-0">{review.comment}</p>}
                    </div>
                    <span className="text-[10px] text-gray-400 shrink-0 sm:ml-3">
                      {new Date(review.created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== FIXED BOTTOM ACTION BAR ===== */}
      {canAct && (
        <div className="fixed bottom-0 left-0 md:left-[var(--sidebar-width,16rem)] right-0 z-40 bg-white border-t border-gray-200 shadow-lg transition-[left] duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="hidden sm:block text-sm text-gray-500 min-w-0">
              <span className="font-medium text-gray-700">{proposal?.title}</span>
              {openCommentCount > 0 && (
                <span className="ml-2 text-orange-500">&middot; {openCommentCount} open comment{openCommentCount !== 1 ? 's' : ''}</span>
              )}
              {isRevising && (
                <span className="ml-2 inline-flex items-center gap-1.5 text-orange-600 font-medium">
                  <span className="w-3 h-3 border-2 border-orange-300 border-t-orange-600 rounded-full animate-spin"></span>
                  Revising…
                </span>
              )}
            </div>
            {/* Every action is suspended while the revision runs — the sections and the
                comment states are both about to change underneath the user. */}
            <div
              className={`flex items-center flex-wrap gap-2 sm:gap-3 sm:flex-shrink-0 ${isRevising ? 'opacity-50 pointer-events-none' : ''}`}
              aria-disabled={isRevising}
            >
              {/* --- Admin review actions (any proposal under review) --- */}
              {canApprove && isPendingQA && (
                <>
                  <button
                    onClick={() => openActionDialog('reject')}
                    disabled={isRevising}
                    className="px-5 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setShowSendBackDialog(true)}
                    disabled={isRevising}
                    className="px-5 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                  >
                    Send Back for Update
                  </button>
                  <button
                    onClick={() => openActionDialog('approve')}
                    disabled={isRevising}
                    className="px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                  >
                    Approve
                  </button>
                </>
              )}

              {/* --- Admin editing flow (draft or sent_back) --- */}
              {canApprove && (isDraft || isSentBack) && (
                <>
                  {openCommentCount > 0 ? (
                    <>
                      <button
                        onClick={handleCancelRevision}
                        disabled={cancelRevisionLoading || isRevising}
                        className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {cancelRevisionLoading ? 'Cancelling...' : 'Cancel Revision'}
                      </button>
                      <button
                        onClick={handleRequestRevision}
                        disabled={isRevising}
                        className="px-5 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isRevising ? 'Revising…' : `Request Revision (${openCommentCount})`}
                      </button>
                    </>
                  ) : proposal?.generation_status === 'completed' ? (
                    <button
                      onClick={() => setShowSubmitForReviewDialog(true)}
                      disabled={isRevising}
                      className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
                    >
                      Send for Approval
                    </button>
                  ) : null}
                </>
              )}

              {/* --- BD Team actions (draft or sent_back) --- */}
              {!canApprove && canSubmitForReview && (isDraft || isSentBack) && (
                <>
                  {openCommentCount > 0 ? (
                    <>
                      <button
                        onClick={handleCancelRevision}
                        disabled={cancelRevisionLoading || isRevising}
                        className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {cancelRevisionLoading ? 'Cancelling...' : 'Cancel Revision'}
                      </button>
                      <button
                        onClick={handleRequestRevision}
                        disabled={isRevising}
                        className="px-5 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isRevising ? 'Revising…' : `Request Revision (${openCommentCount})`}
                      </button>
                    </>
                  ) : proposal?.generation_status === 'completed' ? (
                    <button
                      onClick={() => setShowSubmitForReviewDialog(true)}
                      disabled={isRevising}
                      className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
                    >
                      Send for Approval
                    </button>
                  ) : null}
                </>
              )}

              {/* --- Withdraw from review (submitters who can't approve, e.g. BD Team) --- */}
              {isPendingQA && canSubmitForReview && !canApprove && (
                <button
                  onClick={handleCancelReview}
                  disabled={cancelReviewLoading}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelReviewLoading ? 'Withdrawing...' : 'Withdraw from Review'}
                </button>
              )}

              {/* --- Client Rejected: show revision actions --- */}
              {isClientRejected && (
                <>
                  {openCommentCount > 0 ? (
                    <>
                      <button
                        onClick={handleCancelRevision}
                        disabled={cancelRevisionLoading || isRevising}
                        className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {cancelRevisionLoading ? 'Cancelling...' : 'Cancel Revision'}
                      </button>
                      <button
                        onClick={handleRequestRevision}
                        disabled={isRevising}
                        className="px-5 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isRevising ? 'Revising…' : `Request Revision (${openCommentCount})`}
                      </button>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500">Add section comments to request a revision</span>
                  )}
                </>
              )}

              {/* --- Send to Client (BD Team + Admin on approved proposals) --- */}
              {isApproved && canSendToClient && (
                <button
                  onClick={() => setShowSendToClientModal(true)}
                  disabled={isRevising}
                  className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-600 transition-colors shadow-sm"
                >
                  Send to Client
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== FEEDBACK DIALOG ===== */}
      {actionDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
            <div className={`px-8 py-6 ${actionDialog === 'approve' ? 'bg-green-50 border-b border-green-100' : 'bg-red-50 border-b border-red-100'}`}>
              <div className="flex items-center gap-3">
                {actionDialog === 'approve' ? (
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
                <div>
                  <h3 className={`text-lg font-bold ${actionDialog === 'approve' ? 'text-green-900' : 'text-red-900'}`}>
                    {actionDialog === 'approve' ? 'Approve Proposal' : 'Reject Proposal'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Version {proposal?.version} &middot; {proposal?.title}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-8 py-6 space-y-4">
              <div className="bg-primary-50 border border-primary-100 rounded-lg px-4 py-3">
                <div className="flex items-start gap-2.5">
                  <svg className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-primary-800 leading-relaxed">
                    <span className="font-semibold">Your feedback is required.</span>{' '}
                    We use your detailed feedback to continuously improve our AI system prompts, structuring knowledge, and proposal quality. The more specific your comments, the better our system learns.
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Rate this proposal</label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 shrink-0">1 (Poor)</span>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setActionRating(n)}
                        className={`w-8 h-8 rounded-md text-sm font-semibold transition-colors ${
                          actionRating === n
                            ? actionDialog === 'approve'
                              ? 'bg-green-600 text-white shadow-sm'
                              : 'bg-red-600 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">10 (Excellent)</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {actionDialog === 'approve'
                    ? 'What worked well in this proposal? Any suggestions for future improvements?'
                    : 'What needs to be changed? Please be as specific as possible.'}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <textarea
                  value={actionComment}
                  onChange={(e) => setActionComment(e.target.value)}
                  placeholder={
                    actionDialog === 'approve'
                      ? 'e.g., The regulatory structuring section was thorough and well-researched. The tokenization approach aligns with client requirements. Suggest adding more detail on distribution channels in future proposals...'
                      : 'e.g., The investment structure does not account for the client\'s existing fund setup. Section 3 needs to reference UAE VARA regulations instead of DFSA. Risk assessment is missing key market factors...'
                  }
                  rows={6}
                  autoFocus
                  className={`w-full rounded-lg border shadow-sm focus:border-primary focus:ring-primary text-sm px-4 py-3 resize-none ${
                    actionComment.trim().length > 0 && actionComment.trim().length < 50
                      ? 'border-amber-300'
                      : 'border-gray-300'
                  }`}
                />
                <div className="flex items-center justify-between mt-1.5">
                  <div>
                    {actionComment.trim().length === 0 && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                        </svg>
                        Please provide your feedback before proceeding
                      </p>
                    )}
                    {actionComment.trim().length > 0 && actionComment.trim().length < 50 && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                        </svg>
                        Minimum 50 characters required for meaningful feedback
                      </p>
                    )}
                  </div>
                  <span className={`text-xs font-medium ${
                    actionComment.trim().length >= 50
                      ? 'text-green-600'
                      : actionComment.trim().length > 0
                        ? 'text-amber-600'
                        : 'text-gray-400'
                  }`}>
                    {actionComment.trim().length}/50
                  </span>
                </div>
              </div>
            </div>
            <div className="px-8 py-4 bg-gray-50 flex items-center justify-end gap-3 border-t border-gray-100">
              <button
                onClick={closeActionDialog}
                disabled={actionLoading !== null}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={actionLoading !== null || actionComment.trim().length < 50}
                className={`px-6 py-2.5 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  actionDialog === 'approve'
                    ? 'text-white bg-green-600 hover:bg-green-700'
                    : 'text-white bg-red-600 hover:bg-red-700'
                }`}
              >
                {actionLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    {actionDialog === 'approve' ? 'Approving...' : 'Rejecting...'}
                  </span>
                ) : (
                  actionDialog === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SECTION REVISION CONFIRMATION ===== */}
      <ConfirmationModal
        isOpen={revisionConfirm !== null}
        onClose={() => !revisionLoading && setRevisionConfirm(null)}
        onConfirm={handleConfirmSectionRevision}
        title="Revise This Section"
        message={`Your open comments on Section ${revisionConfirm ?? ''} will be applied to this proposal in place. That section will be rewritten; if a comment corrects a fact, other sections stating the old value are corrected too. No new version is created, and comments on other sections stay open.`}
        confirmText="Yes, Revise Proposal"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={revisionLoading}
      />

      {/* ===== CANCEL REVISION CONFIRMATION ===== */}
      <ConfirmationModal
        isOpen={showCancelRevisionConfirm}
        onClose={() => !cancelRevisionLoading && setShowCancelRevisionConfirm(false)}
        onConfirm={handleConfirmCancelRevision}
        title="Cancel Revision"
        message={`This will dismiss all ${openCommentCount} open comment${openCommentCount !== 1 ? 's' : ''} across the proposal. This action cannot be undone. Continue?`}
        confirmText="Yes, Dismiss All"
        cancelText="Go Back"
        confirmVariant="danger"
        isLoading={cancelRevisionLoading}
      />

      {/* ===== REQUEST REVISION (ALL COMMENTS) CONFIRMATION ===== */}
      <ConfirmationModal
        isOpen={showRevisionConfirm}
        onClose={() => !revisionAllLoading && setShowRevisionConfirm(false)}
        onConfirm={handleConfirmRevisionAll}
        title="Request Proposal Revision"
        message={`Your ${openCommentCount} open comment${openCommentCount !== 1 ? 's' : ''} will be applied to this proposal in place. Sections you commented on will be rewritten; if a comment corrects a fact, other sections stating the old value are corrected too. No new version is created.`}
        confirmText="Yes, Revise Proposal"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={revisionAllLoading}
      />

      {/* ===== SEND BACK FOR UPDATE DIALOG ===== */}
      {showSendBackDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="px-8 py-6 bg-orange-50 border-b border-orange-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-orange-900">Send Back for Update</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Version {proposal?.version} &middot; {proposal?.title}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-8 py-6 space-y-4">
              <div className="bg-orange-50 border border-orange-100 rounded-lg px-4 py-3">
                <p className="text-sm text-orange-800">
                  <span className="font-semibold">This will send the proposal back to the BD team.</span>{' '}
                  They will see your comments and revise it in place. No knowledge or structured data will be updated.
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What needs to be updated? <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={sendBackComment}
                  onChange={(e) => setSendBackComment(e.target.value)}
                  placeholder="e.g., The regulatory section needs to reference DFSA instead of VARA. The distribution strategy doesn't align with the client's existing network..."
                  rows={5}
                  autoFocus
                  className={`w-full rounded-lg border shadow-sm focus:border-primary focus:ring-primary text-sm px-4 py-3 resize-none ${
                    sendBackComment.trim().length > 0 && sendBackComment.trim().length < 20
                      ? 'border-amber-300'
                      : 'border-gray-300'
                  }`}
                />
                <div className="flex items-center justify-between mt-1.5">
                  <div>
                    {sendBackComment.trim().length > 0 && sendBackComment.trim().length < 20 && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                        </svg>
                        Minimum 20 characters required
                      </p>
                    )}
                  </div>
                  <span className={`text-xs font-medium ${
                    sendBackComment.trim().length >= 20 ? 'text-green-600' : 'text-gray-400'
                  }`}>
                    {sendBackComment.trim().length}/20
                  </span>
                </div>
              </div>
            </div>
            <div className="px-8 py-4 bg-gray-50 flex items-center justify-end gap-3 border-t border-gray-100">
              <button
                onClick={() => {
                  setShowSendBackDialog(false);
                  setSendBackComment('');
                }}
                disabled={sendBackLoading}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendBack}
                disabled={sendBackLoading || sendBackComment.trim().length < 20}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendBackLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    Sending Back...
                  </span>
                ) : (
                  'Send Back for Update'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SEND FOR APPROVAL DIALOG ===== */}
      {showSubmitForReviewDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="px-8 py-6 bg-primary-50 border-b border-primary-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-primary-900">Send for Approval</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Version {proposal?.version} &middot; {proposal?.title}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-8 py-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Add a note for the reviewer <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={submitReviewComment}
                  onChange={(e) => setSubmitReviewComment(e.target.value)}
                  placeholder="e.g., Please pay special attention to the regulatory section — client has specific DFSA requirements..."
                  rows={4}
                  autoFocus
                  className="w-full rounded-lg border border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm px-4 py-3 resize-none"
                />
              </div>
            </div>
            <div className="px-8 py-4 bg-gray-50 flex items-center justify-end gap-3 border-t border-gray-100">
              <button
                onClick={() => {
                  setShowSubmitForReviewDialog(false);
                  setSubmitReviewComment('');
                }}
                disabled={submitForReviewLoading}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitForReview}
                disabled={submitForReviewLoading}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitForReviewLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    Submitting...
                  </span>
                ) : (
                  'Send for Approval'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ===== SEND TO CLIENT EMAIL MODAL ===== */}
      <SendToClientModal
        isOpen={showSendToClientModal}
        onClose={() => setShowSendToClientModal(false)}
        onSend={handleSendToClient}
        leadId={leadId}
        proposalId={proposalId}
        isLoading={sendToClientLoading}
      />
      {showNarrationModal && (
        <NarrationEditorModal
          narrations={proposal?.narrations || []}
          narrationsStale={proposal?.narrations_stale}
          onSave={handleSaveNarrations}
          onGenerate={handleGenerateNarrations}
          onClose={() => setShowNarrationModal(false)}
          saving={narrationSaving}
          generating={narrationGenerating}
        />
      )}
    </DashboardLayout>
  );
};

export default ProposalDetailPage;
